/**
 * Ancestor - Root of the framework's inheritance system
 *
 * The foundational class that provides the inherit() method used by all framework classes.
 * Built in 2012-2013 before native JavaScript class inheritance existed, influenced by
 * ActionScript 3 object-oriented patterns.
 *
 * ARCHITECTURE: All framework classes trace back to Ancestor through inheritance.
 * Provides core functionality: class creation, instance construction, lifecycle management,
 * dependency injection, and memory cleanup coordination.
 *
 * PUBLIC API CONTEXT: Methods defined with public: true receive the calling instance
 * as their first argument. Callers do not pass this argument; it is auto-injected.
 *
 * @class Ancestor
 * @since 0.1.0
 *
 * @example
 * // Creating a new class that extends Ancestor
 * const MyClass = Ancestor.inherit({
 *     namespace: 'MyClass',
 *     api: {
 *         myMethod: {
 *             public: true,
 *             value: function(context) {
 *                 // Implementation
 *             }
 *         }
 *     }
 * });
 *
 * @example
 * // Creating a singleton
 * const MySingleton = Ancestor.inherit({
 *     namespace: 'MySingleton',
 *     singleton: true,
 *     // ...
 * });
 */
define("Ancestor", [`require`, `lodash`, `jquery`], function (require, _, jQuery) {
	const DEBUG_THRESHOLD = 5; // only show log info <= DEBUG_THRESHOLD
	var argRange = _.range(0, 30),
		useConsole; //

	// Generate a wrapper function for calling console commands, since it doesn't support things like console.log.apply()
	useConsole = `if(typeof console !== "undefined") {`;
	useConsole += `switch(args.length) {`;
	_.each(argRange, function (i) {
		var innerRange = [];
		for (var j = 0; j < i; j++) {
			innerRange.push(`args[` + j + `]`);
		}

		// manually call console.log(args[0], args[1]), for example
		useConsole += `case ` + i + `:`;
		useConsole += `console[command](`;
		useConsole += innerRange.join(`,`);
		useConsole += `); break;`;
	});
	useConsole += `} }`; // end switch, end if // disable jshint warning for 'using eval', this isn't arbitrary code

	/*jshint -W054 */ useConsole = new Function(`command`, `args`, useConsole);
	/*jshint +W054 */

	/**
	 * Internal logging utility with styled console output
	 *
	 * Provides color-coded console logging with priority filtering. Used throughout
	 * the framework for debugging and development.
	 *
	 * @private
	 * @param {Object|string} options - Log options or simple message string
	 * @param {string} [options.message] - Message to log (supports printf-style formatting)
	 * @param {string} [options.type='message'] - Log type: 'message', 'error', 'success', 'warning'
	 * @param {Array} [options.args] - Arguments for printf-style message formatting
	 * @param {number} [options.priority=5] - Priority level (only logs if <= DEBUG_THRESHOLD)
	 * @returns {void}
	 *
	 * @example
	 * log('Simple message');
	 *
	 * @example
	 * log({
	 *     type: 'error',
	 *     message: 'Error occurred: %O',
	 *     args: [errorObj],
	 *     priority: 1
	 * });
	 */
	function log(options) {
		if (!_.isObject(options)) {
			// allow for easier generic use of this.log('something happened');
			options = {
				message: options,
			};
		}

		if (options.args === undefined) {
			options.args = _.toArray(arguments).splice(1);
		} else {
			options.args = _.toArray(options.args); // in case the "arguments" variable was passed directly
		}

		_.defaults(options, {
			type: `message`,
			message: ``,
			args: [],
			tags: [],
			priority: DEBUG_THRESHOLD, // default to showing if not provided
		});

		if (options.priority > DEBUG_THRESHOLD) {
			return;
		}

		var args = options.args;
		switch (options.type) {
			case `error`:
				options.message = `%c` + options.message;
				args.unshift(`color:red; font-weight:bold;`);
				break;
			case `success`:
				options.message = `%c` + options.message;
				args.unshift(`color:green;`);
				break;
			case `warning`:
				options.message = `%c` + options.message;
				args.unshift(`color:orange;`);
				break;
		}
		args.unshift(options.message);

		useConsole(`log`, args);
	}

	/**
	 * Internal helper for async method handling
	 *
	 * Wraps promises from async-marked methods with standard options handling.
	 * Used internally by the inherit() method when processing async: true methods.
	 *
	 * @private
	 * @param {Promise} promise - Promise to wrap
	 * @param {Object} options - Async options (then, fail, spread, done, owner)
	 * @returns {Promise} Wrapped promise with bound callbacks
	 */
	return {
		/**
		 * Blueprint object defining the structure of a class
		 *
		 * Internal structure used by inherit() to build new classes. Contains all
		 * configuration, lifecycle hooks, and API definitions that will be layered
		 * through inheritance.
		 *
		 * @private
		 */
		blueprint: {
			// the inner configuration for a blank class
			params: [], // stores the set of params for each inheritance level
			namespace: `Ancestor`, // place to occupy in the global namespace
			requires: [], // list of dependencies requested before construction begins
			defaults: {}, // default values that will be layered into the setup object before being passed into the constructors
			prepareHandlers: [], // a series of functions to be run sequentially to represent instance preparation
			constructors: [], // a series of functions to be run sequentially to represent instance construction
			readyHandlers: [], // a series of functions to be run asynchronously once the instance is ready
			destructors: [], // a series of functions to be run in reverse (Input.destructor before Class.destructor) initiated via this.destroy
			api: {}, // the set of defined api functions for the instance (private and public)
		},

		/**
		 * Create a new class that inherits from this class
		 *
		 * This is the core method of the framework's inheritance system. It creates a new
		 * class definition that inherits all functionality from the parent class and adds
		 * new capabilities defined in params.
		 *
		 * PATTERN: Each level of inheritance adds capabilities:
		 * - Ancestor: inherit(), logging, lifecycle
		 * - Class: Utility methods (from lodash)
		 * - Controller: DOM container, event handling
		 * - Module: Template rendering
		 *
		 * @param {Object} params - Class definition parameters
		 * @param {string} params.namespace - REQUIRED: Unique identifier for this class (e.g., 'MyClass', 'Pages/MyPage')
		 * @param {boolean} [params.singleton=false] - If true, immediately constructs and returns instance
		 * @param {string[]} [params.requires] - Array of dependency namespaces to inject before construction
		 * @param {Object} [params.defaults] - Default configuration values merged into setup
		 * @param {Function} [params.prepare] - Lifecycle hook: called after dependencies load, before constructor
		 * @param {Function} [params.constructor] - Lifecycle hook: main initialization logic
		 * @param {Function} [params.ready] - Lifecycle hook: called after construction completes
		 * @param {Function} [params.destructor] - Lifecycle hook: cleanup when instance destroyed
		 * @param {Object} [params.api] - Object defining methods (public/private, static/instance)
		 * @param {Object} [params.data] - Initial data to merge into this.data
		 * @param {Array} [params.children] - Child component configurations
		 * @param {Array} [params.listeners] - Event listener configurations
		 *
		 * @returns {Object|Function} If singleton: instance, otherwise: Class constructor
		 *
		 * @example
		 * // Create instance-based class
		 * const MyClass = ParentClass.inherit({
		 *     namespace: 'MyClass',
		 *     requires: ['XHR', 'Utils'],
		 *     defaults: {
		 *         option: 'defaultValue'
		 *     },
		 *     prepare(setup) {
		 *         this.assign(this.data, {
		 *             items: []
		 *         });
		 *     },
		 *     constructor(setup) {
		 *         const { XHR } = this;
		 *         // Initialization logic
		 *     },
		 *     destructor() {
		 *         // Cleanup logic
		 *     },
		 *     api: {
		 *         myMethod: {
		 *             public: true,
		 *             value: function(context, param) {
		 *                 // Method implementation
		 *             }
		 *         }
		 *     }
		 * });
		 *
		 * @example
		 * // Create singleton (immediately constructed)
		 * const MySingleton = ParentClass.inherit({
		 *     namespace: 'MySingleton',
		 *     singleton: true,
		 *     // Singleton is immediately constructed and returned
		 * });
		 *
		 * @see Ancestor#construct for instance construction details
		 */
		inherit: function (params) {
			// create a new Class, inheriting all functionality from the one being inherited from
			_.defaults(params, {
				singleton: false,
			});

			if (!_.isString(params.namespace)) {
				throw new Error(`Namespace is a required param for Class.inherit(params)`);
			}

			var blueprint = _.cloneDeep(this.blueprint),
				ChildClass;

			blueprint.namespace = params.namespace;
			blueprint.params.push(params); // keep track of all params

			if (_.isObject(params.defaults)) {
				_.assign(blueprint.defaults, params.defaults);
			}

			if (_.isFunction(params.prepare) && params.prepare !== Object.prototype.prepare) {
				blueprint.prepareHandlers.push(params.prepare);
			}

			if (_.isFunction(params.constructor) && params.constructor !== Object.prototype.constructor) {
				blueprint.constructors.push(params.constructor);
			}

			if (_.isFunction(params.ready) && params.ready !== Object.prototype.ready) {
				blueprint.readyHandlers.push(params.ready);
			}

			if (_.isFunction(params.destructor)) {
				blueprint.destructors.push(params.destructor);
			}

			_.each(params.requires, function (namespace) {
				if (namespace !== blueprint.namespace) {
					blueprint.requires.push(namespace);
				}
			});

			_.each(params.api, function (config, name) {
				var existing = blueprint.api[name],
					exists = _.isObject(existing),
					value = config.value; // cache for later below

				if (exists) {
					existing = _.cloneDeep(existing); // protect the parent's version
				}

				_.defaults(config, {
					// all-purpose
					private: true, // expose the function internally (i.e. this.doSomething)
					public: false, // expose the function to external instances (i.e. myCreatedInstance.doSomething)
					enumerable: true, // passed to the Object definition call

					// Function-specific
					static: false, // ignore context altogether
					clone: false, // wrap the function result in a this.clone() call, to protect inner values

					throttle: 0, // throttle the function; only fire every X milliseconds
					leading: true, // fire on the leading edge, the first time it's called
					trailing: true, // fire on the trailing edge, after X milliseconds

					debounce: 0, // debounce the function; pause execution until X milliseconds without calls
					immediate: false, // whether to run immediately, or start pausing without running anything

					// useOptions: true, // if true: function(options<Object>) attaches options.owner; if false: function(context, ...)
					defaults: null, // if a map is provided, will extend arguments[0] (assumes it's an 'options' hash)
					async: false, // wrap the function, to automatically apply async handling and return the promise
				});

				if (_.isFunction(value)) {
					// function-specific, affecting the base blueprint variable
					if (config.clone) {
						if (config.async) {
							(function () {
								// start a new variable scope
								var internalValue = value;

								value = function () {
									var result = internalValue.apply(this, arguments);

									return this.when({
										promise: result,
										then: _.cloneDeep,
									});
								};
							}).call(this);
						} else {
							(function () {
								// start a new variable scope
								var internalValue = value;

								value = function () {
									var result = internalValue.apply(this, arguments);
									return _.cloneDeep(result);
								};
							}).call(this);
						}
					}

					if (config.async) {
						(function () {
							// start a new variable scope
							var internalValue = value; // cache a version of the current function

							value = function () {
								var context = config.public ? arguments[0] : this,
									asyncOptions = {
										owner: context,
									},
									index = 0,
									options;

								if (exists) {
									index++;
								}

								if (config.public) {
									index++;
								}

								options = arguments[index];

								if (!_.isObject(options)) {
									options = {};
								}

								_.defaults(options, {
									// async defaults
									done: false,
								});

								_.each([`then`, `spread`, `fail`, `fin`, `done`], function (key) {
									asyncOptions[key] = options[key]; // pull out only the async-related options
									delete options[key]; // avoid potential double-handling, remove before passing in
								});

								// call the passed handler with the reduced options object
								if (config.public) {
									asyncOptions.promise = internalValue.apply(this, [context, options]);
								} else {
									asyncOptions.promise = internalValue.apply(this, [options]);
								}

								return this.when(asyncOptions);
							};
						}).call(this);
					}

					if (_.isObject(config.defaults) || config.async) {
						(function () {
							// start a new variable scope
							var internalValue = value; // cache a version of the current function

							value = function () {
								var args = _.toArray(arguments), // used to keep the rest of the arguments intact
									index = 0,
									options;

								if (config.public) {
									// ignore inserted context
									index++;
								}

								if (exists) {
									// ignore the return value from the parent function
									index++;
								}

								options = args[index];

								if (!_.isObject(options)) {
									options = {};
									args[index] = options;
								}

								_.defaults(options, _.cloneDeep(config.defaults));
								return internalValue.apply(this, args);
							};
						}).call(this);
					}
				}

				config.value = value;

				if (exists) {
					// already defined in the blueprint
					_.each(config, function (val, key) {
						var internalValue;

						switch (key) {
							case `value`:
								internalValue = existing.value;

								existing.value = function () {
									var returned = internalValue.apply(this, arguments),
										args = _.toArray(arguments),
										index = config.public ? 1 : 0;

									args.splice(index, 0, returned);
									return val.apply(this, args);
								};
								break;

							case `defaults`:
								_.assign(existing.defaults, config.defaults);
								break;

							case `public`:
							case `private`:
							case `static`:
							case `throttle`:
							case `debounce`:
							case `async`:
							default:
								existing[key] = config[key];
								break;
						}
					});

					blueprint.api[name] = existing;
				} else {
					// first time this key is being used
					blueprint.api[name] = _.cloneDeep(config);
				}
			});

			ChildClass = Object.defineProperties(
				{},
				{
					// Copy read-only versions of the required values onto a new object
					blueprint: {
						value: blueprint,
						enumerable: true,
					},
					construct: {
						value: this.construct,
						enumerable: true,
					},
					inherit: {
						value: this.inherit,
						enumerable: true,
					},
				}
			);

			return params.singleton ? ChildClass.construct({}) : ChildClass;
		},

		/**
		 * Construct an instance of this class
		 *
		 * Creates a new instance with complete lifecycle management: dependency injection,
		 * prepare hooks, construction hooks, ready hooks, and cleanup coordination.
		 *
		 * LIFECYCLE SEQUENCE:
		 * 1. Load dependencies (from 'requires' array)
		 * 2. Run prepare() hooks (all inheritance levels)
		 * 3. Run constructor() hooks (all inheritance levels)
		 * 4. Run ready() hooks (asynchronous, after construction)
		 * 5. Instance ready for use
		 *
		 * MEMORY MANAGEMENT: Instances track dependencies and notify them on destruction
		 * via registerDependency() and deathHandlers, enabling automatic cleanup.
		 *
		 * @param {Object} [setup={}] - Configuration object passed to lifecycle hooks
		 * @param {Object} [setup.owner] - Parent instance that created this instance
		 * @param {Function} [setup.then] - Success callback when construction completes
		 * @param {Function} [setup.fail] - Failure callback if construction errors
		 * @param {Object} [setup.data] - Initial data to merge into this.data
		 * @param {Object} [setup.callbacks] - Callbacks for parent instance (die, destroy)
		 * @param {*} [setup.*] - Any custom configuration merged with defaults
		 *
		 * @returns {Promise<Object>} Promise resolving to instance's public API
		 *
		 * @example
		 * // Create instance with configuration
		 * MyClass.construct({
		 *     option: 'value',
		 *     then: function() {
		 *         // Called when construction complete
		 *     }
		 * }).then(function(instance) {
		 *     // Instance ready to use
		 * });
		 *
		 * @example
		 * // Create child instance (automatic parent-child binding)
		 * this.create({
		 *     namespace: 'ChildClass',
		 *     setup: {
		 *         option: 'value'
		 *     }
		 * });
		 */
		construct: function (setup) {
			// construct an instance of the current class
			const instance = {};

			if (_.isUndefined(setup)) {
				setup = {};
			}

			const owner = _.isObject(setup.owner) ? setup.owner : null;
			const blueprint = _.cloneDeep(this.blueprint);
			const { requires } = blueprint;
			const { constructors, prepareHandlers, readyHandlers } = blueprint;
			const data = {};
			const children = [];
			const listeners = []; // whether to block the app, can be set to false before it takes effect
			let then;
			let fail;
			let api;

			let alive = true;
			const deathHandlers = [];
			const deps = [];

			_.each(blueprint.params, function (params) {
				// iterate over the params passed in at each inheritance level
				if (_.isObject(params.data)) {
					_.assign(data, params.data);
				}

				_.each(params.children, function (config) {
					children.push(config);
				});

				_.each(params.listeners, function (config) {
					listeners.push(config);
				});
			});

			if (_.isObject(setup.data)) {
				_.assign(data, setup.data);
			}

			if (_.isFunction(setup.then)) {
				then = _.bind(setup.then, instance);
			}

			if (_.isFunction(setup.fail)) {
				fail = _.bind(setup.fail, instance);
			}

			Object.defineProperties(setup, {
				// inject some extra functionality into the setup object
				data: {
					get: function () {
						return _.cloneDeep(data);
					},
				},
				children: {
					get: function () {
						return _.cloneDeep(children);
					},
				},
				listeners: {
					get: function () {
						return _.cloneDeep(listeners);
					},
				},
			});

			const setupDefaults = _.cloneDeep(blueprint.defaults);
			_.defaults(setup, setupDefaults);

			if (_.isString(setup.name)) {
				instance.name = setup.name;
			}

			Object.defineProperties(instance, {
				namespace: {
					// the complete namespace
					enumerable: true,
					value: blueprint.namespace,
				},

				alive: {
					enumerable: true,
					get: function () {
						return alive;
					},
				},

				pushConstructor: {
					// when passed a function, it will be pushed onto the constructor array
					configurable: true, // allow deletion when construction is complete
					enumerable: true,
					value(fn) {
						if (_.isFunction(fn)) {
							constructors.push(fn); // add to the array of construction functions to run
						} else {
							throw new Error(`setup.pushConstructor(fn) called without an argument`);
						}
					},
				},

				/**
				 * Register a dependency with cleanup notification
				 *
				 * MEMORY MANAGEMENT: This is how the singleton responsibility pattern works.
				 * Singletons register with instances and provide an onDie callback. When the
				 * instance is destroyed, all registered dependencies are notified to clean up
				 * any resources associated with that instance (event listeners, active requests, etc.).
				 *
				 * @function registerDependency
				 * @memberof Ancestor
				 * @instance
				 * @param {Object} options - Registration options
				 * @param {string} options.namespace - Dependency namespace (for tracking)
				 * @param {Function} [options.onDie] - Callback when this instance is destroyed
				 * @returns {void}
				 *
				 * @example
				 * // Singleton registering with instance for cleanup
				 * instance.registerDependency({
				 *     namespace: 'XHR',
				 *     onDie: () => {
				 *         // Clean up any XHR requests for this instance
				 *         this.cancelRequestsFor(instance);
				 *     }
				 * });
				 */
				registerDependency: {
					enumerable: true,
					value: ({ namespace, onDie }) => {
						if (_.includes(deps, namespace)) {
							throw new Error(`Dependency ${namespace} already registered`);
						}

						deps.push(namespace);

						if (_.isFunction(onDie)) {
							deathHandlers.push(onDie);
						}
					},
				},

				dependencies: {
					enumerable: true,
					get: () => _.clone(deps),
				},

				instances: {
					enumerable: true,
					value: {},
				},

				children: {
					enumerable: true,
					value: [],
				},

				promises: {
					enumerable: true,
					value: {},
				},

				log: {
					enumerable: true,
					value: log,
				},

				clone: {
					// deep clones serializable data structures
					enumerable: true,
					value: _.cloneDeep,
				},

				callbacks: {
					// a stash for handlers that should be called at specific times, e.g. notifying the creating class of destruction
					enumerable: true,
					value: _.reduce(
						setup.callbacks,
						function (callbacks, fn, name) {
							// iterate over all the passed callback handlers
							callbacks[name] = function () {
								if (instance.alive || name === `die`) {
									// only allow the 'callbacks.die' function to fire after destroy() is called
									fn.apply(owner, arguments);
								} else {
									try {
										instance.log({
											type: `warning`,
											message: `Dead instance: %O ${name} (%O)`,
											args: [owner.namespace, arguments],
										});
									} catch (err) {
										// swallow error
									}
								}
							}; // bind them to the creating instance

							return callbacks;
						},
						{}
					), // start with a blank object
				},

				/**
				 * Create a child instance with automatic parent-child binding
				 *
				 * Creates a new instance and establishes parent-child relationship. The child
				 * will be automatically destroyed when the parent is destroyed, preventing
				 * memory leaks.
				 *
				 * CLEANUP: Child instances are tracked in this.children and automatically
				 * destroyed during parent's destructor.
				 *
				 * @function create
				 * @memberof Ancestor
				 * @instance
				 * @async
				 * @param {Object} options - Creation options
				 * @param {string} options.namespace - Class to instantiate (e.g., 'Module', 'Pages/MyPage')
				 * @param {Object} [options.setup] - Configuration passed to child's constructor
				 * @param {string} [options.setup.name] - Name for this.instances reference
				 * @param {Function} [options.then] - Success callback
				 * @param {Function} [options.fail] - Error callback
				 * @param {Function} [options.catch] - Error callback (alias for fail)
				 *
				 * @returns {Promise<Object>} Promise resolving to child's public API
				 *
				 * @example
				 * // Create named child instance
				 * this.create({
				 *     namespace: 'Modules/MyModule',
				 *     setup: {
				 *         name: 'myModule',
				 *         container: this.get('.module-container'),
				 *         option: 'value'
				 *     },
				 *     then: function(child) {
				 *         // Access via this.instances.myModule
				 *     }
				 * });
				 */
				create: {
					enumerable: true,
					async value(options) {
						try {
							// create a new instance of the current class, binding child to parent
							const setup = _.isObject(options.setup) ? options.setup : {};

							setup.owner = instance;

							const [Class] = await instance.require({ deps: [options.namespace] });

							if (instance.isUndefined(setup.theme)) {
								if (instance.isString(instance.data.theme)) {
									setup.theme = instance.data.theme;
								}
							}

							const child = await Class.construct(setup); // create a new instance with the passed setup config
							let { name } = child;

							if (!_.isString(name) || name.length === 0) {
								name = setup.name;
							}

							// keep track of all constructed children, to be automatically destroyed when the parent is destructing
							instance.children.push(child);

							if (_.isString(name) && name.length > 0) {
								// if a name is provided, save into this.instances with the name as the key
								instance.instances[name] = child;
							}

							options.promise = child;
							return instance.when(options);
						} catch (error) {
							const errorHandler = this.isFunction(options.catch) ? options.catch : options.fail;

							if (this.isFunction(errorHandler)) {
								return errorHandler(error);
							} else {
								throw error;
							}
						}
					},
				},

				/**
				 * Promise wrapper with automatic context binding
				 *
				 * Wraps Q promises with automatic callback binding to instance context.
				 * Provides consistent promise handling across the framework.
				 *
				 * @function when
				 * @memberof Ancestor
				 * @instance
				 * @param {Object} [options={}] - Promise configuration
				 * @param {Promise} [options.promise] - Single promise to wrap
				 * @param {Promise[]} [options.promises] - Multiple promises (uses Q.all)
				 * @param {Object} [options.owner=this] - Context for callback binding
				 * @param {Function} [options.then] - Success callback (auto-bound to owner)
				 * @param {Function} [options.spread] - Success callback with spread arguments
				 * @param {Function} [options.fail] - Error callback (auto-bound to owner)
				 * @param {Function} [options.fin] - Finally callback (always runs)
				 * @param {boolean} [options.done=false] - If true, don't return promise (fire and forget)
				 *
				 * @returns {Promise|void} Promise if done=false, void if done=true
				 *
				 * @example
				 * // Wrap promise with bound callbacks
				 * return this.when({
				 *     promise: XHR.get('/api/data'),
				 *     then: function(data) {
				 *         this.data.items = data.items;  // 'this' is instance
				 *     },
				 *     fail: function(error) {
				 *         this.handleError(error);  // 'this' is instance
				 *     }
				 * });
				 *
				 * @example
				 * // Wait for multiple promises
				 * this.when({
				 *     promises: [promise1, promise2, promise3],
				 *     then: function() {
				 *         // All promises resolved
				 *     }
				 * });
				 */
				when: {
					enumerable: true,
					value: function (options) {
						if (typeof options === `undefined`) {
							options = {};
						}

						_.defaults(options, {
							owner: instance,
							done: false,
						});

						let promise = _.isArray(options.promises)
							? Promise.all(options.promises)
							: Promise.resolve(options.promise);

						options.catch = options.catch || options.fail;
						options.finally = options.finally || options.fin;

						if (_.isFunction(options.catch)) {
							options.catch = _.bind(options.catch, options.owner);
						}

						if (_.isFunction(options.then)) {
							options.then = _.bind(options.then, options.owner);
							promise = promise.then(options.then, options.catch);
						} else if (_.isFunction(options.spread)) {
							options.spread = _.bind(options.spread, options.owner);
							promise = promise.then(function (result) {
								return options.spread.apply(options.owner, _.isArray(result) ? result : [result]);
							}, options.catch);
						} else if (_.isFunction(options.catch)) {
							promise = promise.catch(options.catch);
						}

						if (_.isFunction(options.finally)) {
							options.finally = _.bind(options.finally, options.owner);
							promise = promise.finally(options.finally);
						}

						if (options.done) {
							return;
						} else {
							return promise;
						}
					},
				},

				/**
				 * Export public API for external use
				 *
				 * Creates a public API object containing only methods marked as public: true.
				 * Used when instance has an owner (parent) to provide controlled access.
				 *
				 * PATTERN: Public methods receive the calling instance as first 'context' argument,
				 * enabling cleanup responsibility tracking.
				 *
				 * @function export
				 * @memberof Ancestor
				 * @instance
				 * @param {Object} ingester - The instance requesting access (parent/owner)
				 * @returns {Object} Public API object with only public methods
				 *
				 * @example
				 * // Internal: Export public API to parent
				 * const publicApi = instance.export(parentInstance);
				 * // publicApi only contains methods with public: true
				 */
				export: {
					enumerable: true,
					value: function (ingester) {
						// returns a map of functions for ingesting instances to use to interact
						var publicApi = {
							get alive() {
								return instance.alive;
							},
							destroy: instance.destroy,
							namespace: instance.namespace,
						};

						_.each(blueprint.api, function (config, name) {
							var value;

							if (config.public === true) {
								value = config.value;

								if (_.isFunction(value)) {
									if (config.static) {
										value = _.bind(value, undefined);
									} else {
										value = _.bind(value, instance, ingester);
									}
								}

								Object.defineProperty(publicApi, name, {
									value,
								});
							}
						});

						return publicApi;
					},
				},

				/**
				 * Destroy this instance and clean up all resources
				 *
				 * Handles complete instance cleanup: destroys children, runs destructors,
				 * notifies dependencies, and prevents further use of the instance.
				 *
				 * CLEANUP SEQUENCE:
				 * 1. Mark instance as not alive (this.alive = false)
				 * 2. Destroy all child instances
				 * 3. Run destructor hooks (reverse order: child → parent)
				 * 4. Notify dependencies via onDie callbacks
				 * 5. Call callbacks.die() if provided
				 *
				 * MEMORY: This is the central cleanup mechanism. All singletons register
				 * deathHandlers to clean up instance-specific resources.
				 *
				 * @function destroy
				 * @memberof Ancestor
				 * @instance
				 * @param {Object} [options={}] - Destruction options
				 * @param {number} [options.delay=0] - Milliseconds to wait before destroying
				 * @param {boolean} [options.done=false] - If true, don't return promise
				 * @param {Function} [options.then] - Callback after destruction
				 * @param {Function} [options.fail] - Error callback
				 *
				 * @returns {Promise<void>|void} Promise if done=false, void if done=true
				 *
				 * @example
				 * // Destroy instance
				 * this.instances.myChild.destroy();
				 *
				 * @example
				 * // Delayed destruction with callback
				 * this.destroy({
				 *     delay: 300,
				 *     then: function() {
				 *         // After destruction complete
				 *     }
				 * });
				 */
				destroy: {
					enumerable: true,
					value: function (options = {}) {
						// instance destruction
						if (!instance.alive) {
							instance.log({
								type: `warn`,
								message: `Destroy() called on previously destroyed instance: ${instance.namespace}`,
							});

							return;
						}

						_.defaults(options, {
							done: false, // whether to end the promise chain internally, or return it to the caller
							delay: 0,
						});

						const { callbacks, children, namespace } = instance;
						let promise;

						if (_.isFunction(callbacks.destroy)) {
							callbacks.destroy(instance);
						}

						alive = false;

						if (options.delay > 0) {
							promise = instance.defer({
								duration: options.delay,
							});
						}

						promise = instance.when({
							promise,
							then: async () => {
								const { destructors } = blueprint;
								const childPromises = [];
								let destructorPromise;

								_.remove(children, (child) => {
									if (child.alive) {
										const childPromise = child.destroy({ hide: false, remove: false });
										childPromises.push(childPromise);
									}

									return true;
								});
								delete instance.children;

								await Promise.all(childPromises);

								// go through the destructor list backward, since the innermost class should have the final word
								for (let i = destructors.length - 1; i >= 0; i--) {
									const destructor = destructors[i];
									destructorPromise = Promise.resolve(destructorPromise).then(() => destructor.call(instance, options));
								}

								if (_.isFunction(callbacks.die)) {
									callbacks.die();
								}

								for (let i = 0; i < deathHandlers.length; i++) {
									// let each dependency know the instance is gone
									const deathHandler = deathHandlers[i];
									deathHandler();
								}

								if (DEBUG_THRESHOLD >= 10) {
									// instance is no longer usable, use a manually call
									useConsole(`log`, [`%c✗ Destroyed: ` + namespace, `color: green;`]);
								}
							},
						});

						options.promise = promise;
						options.owner = instance;
						return instance.when(options);
					},
				},
			});

			_.each(blueprint.api, (config, key) => {
				let { value: internal } = config;
				let value;

				if (_.isFunction(internal)) {
					if (config.throttle > 0) {
						// common example - scroll handler
						internal = _.throttle(internal, config.throttle, {
							leading: config.leading,
							trailing: config.trailing,
						});
					}

					if (config.debounce > 0) {
						// common example - value change after typing
						internal = _.debounce(internal, config.debounce, config.immediate);
					}

					if (config.static) {
						internal = _.bind(internal, undefined);
					} else {
						internal = _.bind(internal, instance);
					}
				}

				if (config.private) {
					value = internal;

					if (_.isFunction(value) && config.public && !config.static) {
						value = _.partial(value, instance); // insert the internal context as the first argument
					}

					Object.defineProperty(instance, key, {
						value,
						enumerable: config.enumerable,
					});
				}
			});

			function loadDependencies() {
				if (requires.length > 0) {
					return instance.require({ deps: requires });
				}
			}

			function runPrepare() {
				if (prepareHandlers.length > 0) {
					// there is more constructing to do
					const nextLayer = prepareHandlers.shift(); // grab the next constructors[0]
					const layerPromise = nextLayer.call(instance, setup);
					return Promise.resolve(layerPromise).then(runPrepare); // pause for deferred actions
				} else {
					// do a low-priority log
					instance.log({
						priority: 10,
						type: `success`,
						message: `✔ Prepared: ${instance.namespace}`,
					});
				}
			}

			function runConstructors() {
				if (constructors.length > 0) {
					// there is more constructing to do
					const nextLayer = constructors.shift(); // grab the next constructors[0]
					const layerPromise = nextLayer.call(instance, setup);
					return Promise.resolve(layerPromise).then(runConstructors); // pause for deferred actions
				} else {
					// all constructors have run, instance is ready

					// overwrite this.pushConstructor(), to catch bad usage
					Object.defineProperty(instance, `pushConstructor`, {
						enumerable: false, // make less prominent,
						value() {
							throw new Error(`[${instance.namespace}] too late for this.pushConstructor()`);
						},
					});

					// do a low-priority log
					instance.log({
						priority: 10,
						type: `success`,
						message: `✔ Constructed: ${instance.namespace}`,
					});
				}
			}

			function runReady() {
				if (readyHandlers.length > 0) {
					// there is more constructing to do
					const nextLayer = readyHandlers.shift(); // grab the next constructors[0]
					const layerPromise = nextLayer.call(instance, setup);
					return Promise.resolve(layerPromise).then(runReady); // pause for deferred actions
				} else {
					// do a low-priority log
					instance.log({
						priority: 10,
						type: `success`,
						message: `✔ Ready: ${instance.namespace}`,
					});
				}
			}

			api = _.isObject(owner) ? instance.export(owner) : instance;
			instance.api = api; // add internal reference to the public api, so it can be adjusted on the fly

			return Promise.resolve(loadDependencies())
				.then(runPrepare)
				.then(runConstructors)
				.then(() => {
					// NOTE: defer and don't inject ready handlers into the promise chain
					runReady();
				})
				.then(then, fail) // trigger the success / fail setup handler
				.catch((error) => {
					log(`Construction error (${instance.namespace}): %O`, error);
					throw error;
				})
				.then(() => api); // success! resolve its public api
		},
	};
});

//# sourceMappingURL=Ancestor.js.map